import { useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { DeviceFrame } from './DeviceFrame';
import type { EditorConfig, EditorNode, GovernancePort, PreviewFormat, Proposal } from './editor-types';
import { GovernancePanel, ChangeBadge, FindingList } from './GovernancePanel';
import { canRedo, canUndo, commit, redo, startHistory, undo, type EditorHistory } from './history';
import { formatPointer, getAt, insertAt, moveAcross, parsePointer, removeAt, setAt, type Pointer } from './json-pointer';
import { LayerTree } from './LayerTree';
import { PropertyPanel } from './PropertyPanel';
import { IconButton } from './StudioPanels';
import './editor.css';

/**
 * Coquille générique de l'éditeur du Studio : arbre des blocs, propriétés générées du schéma, aperçu en
 * direct par le renderer du métier, validation, impact et publication. Brouillon en mémoire, annuler et
 * refaire (Ctrl+Z, Ctrl+Y sur l'éditeur, jamais un raccourci global). Le métier fournit `config` et `port`.
 */

type Panel = 'properties' | 'findings' | 'governance';

function flatten<D>(node: EditorNode<D>, out: EditorNode<D>[] = []): EditorNode<D>[] {
  out.push(node);
  for (const child of node.children) flatten(child, out);
  return out;
}

/** Pointeur d'un bloc après déplacement : sa place dans la liste cible, décalée si la source la précédait dans un parent commun. */
export function locateMoved(from: { list: Pointer; index: number }, to: { list: Pointer; index: number }): Pointer {
  if (from.list === to.list) return `${to.list}/${to.index}`;
  const prefix = `${from.list}/`;
  let list = to.list;
  if (list.startsWith(prefix)) {
    const [head, ...rest] = list.slice(prefix.length).split('/');
    const position = Number(head);
    if (Number.isInteger(position) && position > from.index) list = `${prefix}${position - 1}${rest.length > 0 ? `/${rest.join('/')}` : ''}`;
  }
  return `${list}/${to.index}`;
}

export function EditorShell<D>({ title, subtitle, layer, base, initial, config, port, onPublished, aside }: {
  title: string;
  subtitle?: string;
  /** Pastille de couche (« Client », « Pack Gibier, surchargé par le client »). */
  layer: string;
  /** Version active de référence (diff, classement) ; `null` pour un document neuf. */
  base: D | null;
  initial: D;
  config: EditorConfig<D>;
  port: GovernancePort<D>;
  onPublished?: (proposal: Proposal) => void;
  /** Bandeau du métier au-dessus de l'éditeur (surcharge d'un gabarit de Pack…). */
  aside?: ReactNode;
}) {
  const [history, setHistory] = useState<EditorHistory<D>>(() => startHistory(initial));
  const [selected, setSelected] = useState<Pointer>('');
  const [panel, setPanel] = useState<Panel>('properties');
  const formats = config.formats;
  const [formatId, setFormatId] = useState<string>(formats[0]?.id ?? '');
  const format: PreviewFormat = formats.find((candidate) => candidate.id === formatId) ?? formats[0] ?? { id: 'fluid', label: 'Aperçu' };
  const [previewKey, setPreviewKey] = useState(0);
  const draft = history.present;

  const tree = useMemo(() => config.tree(draft), [config, draft]);
  const nodes = useMemo(() => flatten(tree), [tree]);
  const current = nodes.some((node) => node.pointer === selected) ? selected : '';
  const group = useMemo(() => config.properties(draft, current), [config, draft, current]);
  const findings = useMemo(() => config.localFindings(draft), [config, draft]);
  const estimate = useMemo(() => config.classify(base, draft), [config, base, draft]);
  const counts = useMemo(() => {
    const map = new Map<Pointer, { errors: number; warnings: number }>();
    for (const finding of findings) {
      const pointer = config.nodeOf(draft, finding.path);
      const entry = map.get(pointer) ?? { errors: 0, warnings: 0 };
      if (finding.severity === 'error') entry.errors += 1;
      else entry.warnings += 1;
      map.set(pointer, entry);
    }
    return map;
  }, [config, draft, findings]);
  const errors = findings.filter((finding) => finding.severity === 'error').length;

  const apply = (next: D, coalesce?: string): void => setHistory((state) => commit(state, next, coalesce));
  const locate = (path: string): void => {
    setSelected(config.nodeOf(draft, path));
    setPanel('properties');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (!(event.ctrlKey || event.metaKey)) return;
    const key = event.key.toLowerCase();
    if (key === 'z' && !event.shiftKey) {
      event.preventDefault();
      setHistory(undo);
    } else if (key === 'y' || (key === 'z' && event.shiftKey)) {
      event.preventDefault();
      setHistory(redo);
    }
  };

  return (
    <section className="ay-editor" aria-label={`Éditeur : ${title}`} onKeyDown={onKeyDown}>
      <header className="ay-editor__header">
        <div className="ay-editor__heading">
          <h2 className="ay-editor__title">{title}</h2>
          <span className="ay-badge">{layer}</span>
          <ChangeBadge change={estimate} estimated {...(port.wording?.minor === undefined ? {} : { minor: port.wording.minor })} />
          {subtitle !== undefined && <span className="ay-text ay-text--muted">{subtitle}</span>}
        </div>
        <div className="ay-editor__actions">
          <IconButton icon="undo" label="Annuler" keys="Ctrl Z" keysInName={false} shortcut="Control+Z" disabled={!canUndo(history)} onClick={() => setHistory(undo)} className="ay-editor__icon" />
          <IconButton icon="redo" label="Rétablir" keys="Ctrl Y" keysInName={false} shortcut="Control+Y" disabled={!canRedo(history)} onClick={() => setHistory(redo)} className="ay-editor__icon" />
          <span className="ay-editor__status" role="status">{errors > 0 ? `${errors} erreur(s)` : 'Brouillon cohérent'}</span>
        </div>
      </header>
      {aside}
      <div className="ay-editor__body">
        <LayerTree
          root={tree}
          selected={current}
          counts={counts}
          onSelect={(pointer) => {
            setSelected(pointer);
            setPanel('properties');
          }}
          onInsert={(slot, kind) => {
            const list = getAt(draft, slot.list);
            const index = Array.isArray(list) ? list.length : 0;
            apply(insertAt(draft, slot.list, kind.create(draft)));
            setSelected(`${slot.list}/${index}`);
          }}
          onMove={(node, target) => {
            if (node.position === undefined) return;
            const next = moveAcross(draft, { list: node.position.list, index: node.position.index }, target);
            if (next === draft) return;
            apply(next);
            setSelected(locateMoved(node.position, target));
          }}
          onRemove={(node) => {
            apply(removeAt(draft, node.pointer));
            const segments = parsePointer(node.pointer);
            setSelected(segments.length > 2 ? formatPointer(segments.slice(0, -2)) : '');
          }}
        />
        <div className="ay-editor__center">
          <div className="ay-editor__tabs" role="tablist" aria-label="Panneaux">
            {([['properties', 'Propriétés'], ['findings', `Contrôles${findings.length > 0 ? ` (${findings.length})` : ''}`], ['governance', 'Validation et publication']] as const).map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={panel === id} className={`ay-editor__tab${panel === id ? ' is-active' : ''}`} onClick={() => setPanel(id)}>{label}</button>
            ))}
          </div>
          <div className="ay-editor__panel" role="tabpanel">
            {panel === 'properties' && (group === null ? <p className="ay-text ay-text--muted">Choisissez un bloc dans l’arbre.</p> : <PropertyPanel group={group} document={draft} onChange={(pointer, value, coalesce) => apply(setAt(draft, pointer, value), coalesce === undefined ? undefined : `${pointer}`)} />)}
            {panel === 'findings' && <FindingList findings={findings} onLocate={locate} empty="Aucun constat local. La validation par le serveur reste à faire avant de proposer." />}
            {panel === 'governance' && <GovernancePanel port={port} draft={draft} dirty={estimate.changes.length > 0} localErrors={errors} estimate={estimate} onLocate={locate} onPublished={(proposal) => onPublished?.(proposal)} />}
          </div>
        </div>
        <aside className="ay-editor__preview" aria-label={`Aperçu du ${config.noun.toLowerCase()}`}>
          <div className="ay-editor__preview-bar">
            <div className="ay-editor__seg" role="radiogroup" aria-label="Format d’aperçu">
              {formats.map((candidate) => (
                <button key={candidate.id} type="button" role="radio" aria-checked={format.id === candidate.id} className={format.id === candidate.id ? 'is-on' : ''} onClick={() => setFormatId(candidate.id)}>{candidate.label}</button>
              ))}
            </div>
            <button type="button" className="ay-editor__tool" onClick={() => setPreviewKey((key) => key + 1)}>Vider l’essai</button>
          </div>
          <DeviceFrame format={format}>
            <div key={previewKey} className="ay-editor__preview-content">{config.renderPreview(draft, format)}</div>
          </DeviceFrame>
        </aside>
      </div>
    </section>
  );
}
